import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, Sparkles } from "lucide-react";
import { API } from "@/lib/api";

const AVATAR =
  "https://static.prod-images.emergentagent.com/jobs/8c354384-f67c-461d-9f4e-17723da72ad0/images/4fed852a910cbe69d977a2ecafa98e4e31b7ac1ab1e6a867155d488f47f0da2d.jpeg";

const CHIPS = [
  "Explain AOX gas fees",
  "How do I mine AOX Bucks?",
  "What's a hoodie node?",
  "Give me a mining strategy",
];

export function MickChat() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Kia ora, hello world! I'm AI Mick — your Super AI Bot on the AOX Blockchain. Ask me anything about hash blocks, mining AOX Bucks, or becoming a verified hoodie node.",
    },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() => `mick-${Math.random().toString(36).slice(2)}`);
  const endRef = useRef(null);
  const scrollRef = useRef(null);

  const scrollDown = () => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  };
  useEffect(scrollDown, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data:")) continue;
          const payload = part.slice(5);
          if (payload === "[DONE]") continue;
          if (payload.startsWith("__session__")) continue;
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = {
              role: "assistant",
              content: copy[copy.length - 1].content + payload,
            };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "My uplink glitched — try that again, hoodie.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <section id="chat" className="relative py-20 px-4 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <SectionHeader
          eyebrow="Dynamic Interaction"
          title="Chat with the Super AI Bot"
          desc="Real-time conversation powered by GPT — Mick answers in character, 24/7."
        />

        <div className="bg-aox-card border border-aox-cyan/25 clip-chamfer overflow-hidden">
          {/* terminal bar */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-aox-cyan/15 bg-aox-elevated/50">
            <span className="w-3 h-3 rounded-full bg-red-500/70" />
            <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
            <span className="w-3 h-3 rounded-full bg-aox-green/70" />
            <span className="font-mono-aox text-xs text-aox-cyan/60 ml-2">
              mick@aox-ledger:~$ live_uplink
            </span>
          </div>

          <div
            ref={scrollRef}
            className="h-[440px] overflow-y-auto p-5 space-y-5"
            data-testid="mick-chat-window"
          >
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}
              >
                {m.role === "assistant" && (
                  <img
                    src={AVATAR}
                    alt="Mick"
                    className="w-9 h-9 rounded-full object-cover border border-aox-cyan/40 flex-shrink-0"
                  />
                )}
                <div
                  className={`max-w-[78%] px-4 py-3 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-aox-cyan/15 border border-aox-cyan/30 text-white"
                      : "bg-aox-elevated border border-aox-green/20 text-slate-200"
                  }`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {m.content ||
                    (streaming && i === messages.length - 1 ? (
                      <Loader2 className="animate-spin text-aox-cyan" size={16} />
                    ) : (
                      ""
                    ))}
                </div>
              </motion.div>
            ))}
            <div ref={endRef} />
          </div>

          {/* chips */}
          <div className="flex gap-2 flex-wrap px-5 pb-3">
            {CHIPS.map((c, i) => (
              <button
                key={c}
                onClick={() => send(c)}
                disabled={streaming}
                className="flex items-center gap-1.5 text-xs font-mono-aox border border-aox-cyan/25 text-aox-cyan/80 px-3 py-1.5 rounded-full hover:bg-aox-cyan/10 hover:text-aox-cyan transition-colors disabled:opacity-50"
                data-testid={`chat-chip-${i}`}
              >
                <Sparkles size={12} />
                {c}
              </button>
            ))}
          </div>

          {/* input */}
          <div className="flex gap-2 p-4 border-t border-aox-cyan/15 bg-aox-elevated/40">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask Mick anything…"
              className="flex-1 bg-aox-bg border border-aox-cyan/20 rounded-md px-4 py-3 text-sm text-white outline-none focus:border-aox-cyan focus:ring-2 focus:ring-aox-cyan/40 transition"
              data-testid="mick-chat-input"
            />
            <button
              onClick={() => send()}
              disabled={streaming}
              className="bg-aox-cyan text-aox-bg font-bold px-5 rounded-md hover:glow-cyan transition-all disabled:opacity-60 grid place-items-center"
              data-testid="mick-chat-send-btn"
            >
              {streaming ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function SectionHeader({ eyebrow, title, desc }) {
  return (
    <div className="mb-10">
      <div className="font-mono-aox text-xs uppercase tracking-[0.3em] text-aox-cyan mb-3">
        // {eyebrow}
      </div>
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-3">
        {title}
      </h2>
      {desc && <p className="text-base text-slate-400 max-w-2xl">{desc}</p>}
    </div>
  );
}
